type Rgb = { r: number, g: number, b: number }

const clamp = (value: number, min = 0, max = 255): number => Math.min(max, Math.max(min, value))

export const isHexColor = (value: string | null | undefined): value is string =>
    typeof value === 'string' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim())

export const hexToRgb = (hex: string): Rgb => {
    let clean = (isHexColor(hex) ? hex.trim() : '#F7B32B').slice(1)

    if (clean.length === 3) clean = clean.split('').map(char => char + char).join('')

    const value = parseInt(clean, 16)

    return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 }
}

export const rgbToHex = ({ r, g, b }: Rgb): string =>
    `#${[r, g, b].map(channel => Math.round(clamp(channel)).toString(16).padStart(2, '0')).join('')}`

export const mix = (from: string, to: string, amount: number): string => {
    const a = hexToRgb(from)
    const b = hexToRgb(to)
    const t = Math.min(1, Math.max(0, amount))

    return rgbToHex({ r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t })
}

export const lighten = (hex: string, amount: number): string => mix(hex, '#ffffff', amount)

export const darken = (hex: string, amount: number): string => mix(hex, '#000000', amount)

export const rgba = (hex: string, alpha: number): string => {
    const { r, g, b } = hexToRgb(hex)

    return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/* Relative luminance, 0 (black) to 1 (white) */
export const luminance = (hex: string): number => {
    const { r, g, b } = hexToRgb(hex)
    const channel = (value: number) => {
        const normalized = value / 255

        return normalized <= 0.03928 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4)
    }

    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

export const isLight = (hex: string): boolean => luminance(hex) > 0.42

export const safeHex = (value: string | null | undefined, fallback: string): string =>
    isHexColor(value) ? value.trim() : fallback
